import { z } from 'zod';
import { isoDateSchema } from './schemas.js';
/**
 * Durable review state for facts read from meeting notes (remediation spec
 * 2026-09-17 §4, §13, §14 — docs/review/2026-09-17-Meeting-Notes-Extraction-
 * Remediation-Specification.docx).
 *
 * An import used to be all-or-nothing per row: what the document contract
 * could hold was merged, and everything else — a care concern, an unsplit ISA
 * total, a purchase the client is still planning, a Scottish address — lived
 * only in the import panel and vanished on reload. This is where those facts
 * persist, with the evidence behind them and the adviser's decision on each.
 *
 * DOCUMENT-ONLY, like `entry`: the projection never reads it, and
 * `withoutFactFindEntry` strips it before any projection request is built, so
 * it can never reach an `inputs_snapshot`. What it CAN do is block — see
 * `importReviewBlockers`, which the projection, review-lock and readiness
 * paths consult so an unresolved region fact stops calculation server-side
 * rather than relying on a disabled button.
 *
 * The note is never stored — neither whole nor in excerpts. Evidence is kept
 * as end-exclusive UTF-16 offsets into the note whose SHA-256 is on the import
 * record (`text` is always empty when written by the importer), beside a
 * system-written summary. The notes-privacy promise (`notesPrivacy.ts`) stays
 * true; the store is bounded (`IMPORT_REVIEW_MAX_BYTES`) so the document stays
 * inside its size CHECK.
 */
export const IMPORT_REVIEW_FACT_TYPES = [
    'dependant_group',
    'expected_child',
    'education_fees',
    'cash',
    'income_component',
    'employment_descriptor',
    'rental_income',
    'combined_holding_total',
    'property_purchase_intent',
    'care_concern',
    'estate_planning_concern',
    'gifting_intention',
    'retirement_range',
    'state_pension_entitlement',
    'student_loan',
    'tax_region',
    'money_mention',
    'other',
];
export const importReviewDispositionSchema = z.enum([
    'mapped',
    'incomplete',
    'ambiguous',
    'intent',
    'unsupported',
    'excluded',
    'superseded',
]);
/** What the adviser has done with a fact. `unreviewed` is the only open state. */
export const importReviewStatusSchema = z.enum([
    'unreviewed',
    'confirmed',
    'corrected',
    'rejected',
]);
const evidenceSpanSchema = z
    .object({
    /** End-exclusive UTF-16 offsets into the note whose hash is on the import. */
    startChar: z.number().int().nonnegative(),
    endChar: z.number().int().nonnegative(),
    /** The note's own words at those offsets, bounded. */
    text: z.string().max(200),
})
    .strict()
    .refine((s) => s.endChar >= s.startChar, 'endChar must be >= startChar');
const factFieldSchema = z
    .object({
    name: z.string().min(1).max(60),
    value: z.union([z.string().max(200), z.number(), z.boolean(), z.null()]),
    unit: z.enum(['pence', 'bps', 'year', 'age', 'count', 'text']).optional(),
    evidenceStatus: z.enum(['stated', 'approximate', 'derived', 'missing', 'disputed']),
})
    .strict();
/** One import of one note into this document. */
export const importRecordSchema = z
    .object({
    /** `imp-<first 16 hex of the note hash>-<asOfDate>` — the same note read
     * against the same date is the same import, so a re-import is idempotent. */
    importId: z.string().regex(/^imp-[0-9a-f]{16}-\d{4}-\d{2}-\d{2}$/),
    inputSha256: z.string().regex(/^[0-9a-f]{64}$/),
    asOfDate: isoDateSchema,
    parserVersion: z.string().regex(/^\d+\.\d+\.\d+$/),
    contractVersion: z
        .string()
        .regex(/^\d+\.\d+\.\d+$/)
        .optional(),
    modelVersion: z.string().min(1).max(200).optional(),
})
    .strict();
export const importReviewFactSchema = z
    .object({
    /** `<importId>:<engine factId or item id>` — stable for the same import.
     * Shape-checked: ids reach the audit log, so they can never carry text. */
    id: z
        .string()
        .regex(/^imp-[0-9a-f]{16}-\d{4}-\d{2}-\d{2}:[A-Za-z0-9_.:-]{1,120}$/, 'not a review fact id'),
    importId: z.string().regex(/^imp-[0-9a-f]{16}-\d{4}-\d{2}-\d{2}$/),
    factType: z.enum(IMPORT_REVIEW_FACT_TYPES),
    disposition: importReviewDispositionSchema,
    temporalStatus: z.enum(['current', 'future', 'historic', 'scenario', 'rejected', 'unknown']),
    /** One neutral sentence — never advice. */
    summary: z.string().min(1).max(300),
    reason: z.string().min(1).max(300).optional(),
    /** Document people/dependant ids the fact is about ('client', 'partner', dependant ids). */
    personIds: z.array(z.string().min(1).max(120)).max(10).optional(),
    /** Document rows this fact produced or relates to — the editor a review card links to. */
    destinationIds: z.array(z.string().min(1).max(120)).max(10).optional(),
    fields: z.array(factFieldSchema).max(20).optional(),
    evidence: z.array(evidenceSpanSchema).min(1).max(8),
    /**
     * `blocking` facts stop calculation and presentation until resolved (a
     * suspected Scottish taxpayer is the case today). Everything else is a
     * review card that never blocks the plan.
     */
    blocking: z.boolean(),
    reviewStatus: importReviewStatusSchema,
    /** The adviser's answer, as a choice id or a short value ("rest_of_uk"). */
    answer: z.string().min(1).max(200).optional(),
    reviewedOn: isoDateSchema.optional(),
})
    .strict();
/** Serialised ceiling for the review store, in UTF-8 bytes of its JSON. */
export const IMPORT_REVIEW_MAX_BYTES = 128_000;
/** UTF-8 byte length of a string — no TextEncoder, so it runs in every build target. */
export function utf8ByteLength(text) {
    let bytes = 0;
    for (let i = 0; i < text.length; i += 1) {
        const code = text.charCodeAt(i);
        if (code < 0x80)
            bytes += 1;
        else if (code < 0x800)
            bytes += 2;
        else if (code >= 0xd800 && code <= 0xdbff && i + 1 < text.length) {
            bytes += 4; // a surrogate pair is one four-byte code point
            i += 1;
        }
        else
            bytes += 3;
    }
    return bytes;
}
export const importReviewSchema = z
    .object({
    imports: z.array(importRecordSchema).max(50),
    facts: z.array(importReviewFactSchema).max(300),
})
    .strict()
    .superRefine((review, ctx) => {
    // Held well inside the fact-find's 256KB database cap (migration 0014):
    // the writer trims resolved provenance to stay under its own 96KB budget,
    // so only open facts can ever push a store past this.
    if (utf8ByteLength(JSON.stringify(review)) > IMPORT_REVIEW_MAX_BYTES) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Too many facts from meeting notes are waiting for review to save them all — resolve some of the open ones, then import again.',
        });
    }
    const imports = new Set(review.imports.map((i) => i.importId));
    const seen = new Set();
    review.facts.forEach((f, n) => {
        if (!imports.has(f.importId)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['facts', n, 'importId'],
                message: `unknown import ${f.importId}`,
            });
        }
        if (seen.has(f.id)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['facts', n, 'id'],
                message: `duplicate review fact ${f.id}`,
            });
        }
        seen.add(f.id);
        if (f.reviewStatus !== 'unreviewed' && f.reviewedOn === undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['facts', n, 'reviewedOn'],
                message: 'a reviewed fact records when it was reviewed',
            });
        }
    });
});
/** A retained fact that still needs the adviser's decision. */
export function isOpenReviewFact(fact) {
    return (fact.reviewStatus === 'unreviewed' &&
        fact.disposition !== 'mapped' &&
        fact.disposition !== 'excluded' &&
        fact.disposition !== 'superseded');
}
/**
 * Facts that must be resolved before the plan may be calculated or presented
 * (spec §8). Consulted by the projection route, the review lock and the
 * readiness gate — the same rule at every boundary, because a disabled UI
 * control is not a server-side guarantee.
 *
 * A region fact is resolved only by an explicit adviser answer that the
 * product supports (`rest_of_uk`). An answer of Scotland keeps it blocking:
 * the product cannot calculate Scottish income tax, and it never substitutes
 * a different region.
 */
export function importReviewBlockers(review) {
    if (review === undefined || review === null)
        return [];
    const out = [];
    for (const fact of review.facts) {
        if (!fact.blocking)
            continue;
        // A tax-region block lifts ONLY on an explicit rest-of-UK answer, whatever
        // the review status says: "rejected" (or any other status) with no such
        // answer would otherwise put a Scottish client on rUK bands through a
        // direct save (adviser review 2026-09-18). Other blocking facts resolve on
        // any recorded decision.
        const resolved = fact.factType === 'tax_region'
            ? fact.reviewStatus !== 'unreviewed' && fact.answer === 'rest_of_uk'
            : fact.reviewStatus !== 'unreviewed';
        if (resolved)
            continue;
        out.push({
            factId: fact.id,
            factType: fact.factType,
            message: fact.factType === 'tax_region'
                ? fact.answer === 'scotland'
                    ? 'Scottish income tax is recorded for this client. This tool models only England, Wales and Northern Ireland income tax, so this plan cannot be calculated or presented.'
                    : 'The meeting note suggests the client may pay Scottish income tax. Confirm the tax region before the plan is calculated or presented.'
                : 'An imported fact still needs review before the plan is calculated. Open the client’s imported facts to resolve it.',
        });
    }
    return out;
}
/** The import id for a note hash read against a date. */
export function importIdFor(inputSha256, asOfDate) {
    return `imp-${inputSha256.slice(0, 16)}-${asOfDate}`;
}
//# sourceMappingURL=import-review.js.map